import { VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useGetGatewayAccountsByAccount,
  useImportPaymentMethods,
  useSearchForImportablePaymentMethods,
} from '../../../api/accountsService';
import useCurrentPaymentGateways from '../../../hooks/useCurrentPaymentGateways';

import {
  MAlertTag,
  MBox,
  MButton,
  MCenterModal,
  MDivider,
  MFlex,
  MFormField,
  MInput,
  MStack,
  MText,
} from '../../Monetize';

import {
  IImportPaymentMethodReqSchema,
  ImportPaymentMethodReqSchema,
  ImportStep,
} from '@monetize/types/app';
import { ImportPaymentMethodList } from './ImportPaymentMethodList';

const formDefault: Partial<IImportPaymentMethodReqSchema> = {
  gatewayAccountId: '',
  gatewayId: '',
};

const PAYMENT_ACCOUNT_ID_TOOLTIP =
  'Provide the account or customer ID from the payment gateway';

const LINKED_PAYMENT_ACCOUNT_ID_TOOLTIP =
  'This account is already linked to a customer in the payment gateway, so payment methods must be imported from that customer.';

interface ImportPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
}

export const ImportPaymentMethodModal = React.forwardRef(
  ({ isOpen, onClose, accountId }: ImportPaymentMethodModalProps, ref) => {
    const [step, setStep] = useState<ImportStep>(
      ImportStep.SELECT_GATEWAY_ACCOUNT_ID,
    );
    const [selectedPMs, setSelectedPMs] = useState<string[]>([]);
    // Keeps the automatic search to one call: onSuccess resets selectedPMs, so
    // a second fire would re-check methods the user had already unchecked.
    const hasSearchedLinkedCustomer = useRef(false);
    const {
      mutate: searchImportablePaymentMethods,
      mutateAsync: doGetImportablePaymentMethod,
      isPending: isLoadingImportablePaymentMethod,
      error: errorImportablePaymentMethod,
      data: dataImportablePaymentMethod,
    } = useSearchForImportablePaymentMethods({
      onSuccess: (data) => {
        if (data.paymentMethods.length === 0) {
          return;
        }
        setStep(ImportStep.SELECT_PAYMENT_METHOD);
        setSelectedPMs(data.paymentMethods.map((pm) => pm.externalId));
      },
    });
    const {
      mutateAsync: doSaveImportPaymentMethod,
      isPending: isLoadingSaveImportPaymentMethod,
      error: errorSaveImportPaymentMethod,
      data: dataSaveImportPaymentMethod,
    } = useImportPaymentMethods({
      onSuccess: (data) => {
        setStep(ImportStep.SUBMITTED_PAYMENT_METHOD);
      },
      onError: () => {
        setStep(ImportStep.SUBMITTED_PAYMENT_METHOD);
      },
    });

    const {
      handleSubmit,
      control,
      formState: { errors, isValid },
      watch,
      setValue,
    } = useForm<IImportPaymentMethodReqSchema>({
      resolver: zodResolver(ImportPaymentMethodReqSchema),
      mode: 'onBlur',
      defaultValues: formDefault,
    });
    const watchGatewayId = watch('gatewayId');
    const watchGatewayAcccountId = watch('gatewayAccountId');

    const { currentPaymentGateway } = useCurrentPaymentGateways({
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

    const { data: gatewayAccounts, isLoading: isLoadingGatewayAccounts } =
      useGetGatewayAccountsByAccount(accountId, {
        enabled: isOpen && !!accountId,
      });

    const gatewayCustomerId = currentPaymentGateway
      ? gatewayAccounts?.find(
          ({ paymentGatewayId }) =>
            paymentGatewayId === currentPaymentGateway.id,
        )?.gatewayCustomerId
      : undefined;

    useEffect(() => {
      currentPaymentGateway && setValue('gatewayId', currentPaymentGateway?.id);
    }, [currentPaymentGateway, setValue]);

    // Validate on set: a disabled field is never blurred, so nothing else would
    // refresh formState.isValid for the Continue button.
    useEffect(() => {
      gatewayCustomerId &&
        setValue('gatewayAccountId', gatewayCustomerId, {
          shouldValidate: true,
        });
    }, [gatewayCustomerId, setValue]);

    // The Payment Account ID is known, so skip asking for it and look up the
    // linked customer's payment methods straight away.
    useEffect(() => {
      if (
        hasSearchedLinkedCustomer.current ||
        !gatewayCustomerId ||
        !currentPaymentGateway
      ) {
        return;
      }
      hasSearchedLinkedCustomer.current = true;
      searchImportablePaymentMethods({
        accountId,
        gatewayId: currentPaymentGateway.id,
        gatewayAccountId: gatewayCustomerId,
      });
    }, [
      accountId,
      currentPaymentGateway,
      gatewayCustomerId,
      searchImportablePaymentMethods,
    ]);

    // Until we know whether a customer is linked — and while its payment
    // methods load — showing the Payment Account ID step would be a flash of
    // a step the user never needs to complete.
    const isResolvingLinkedCustomer =
      isLoadingGatewayAccounts ||
      (!!gatewayCustomerId &&
        !dataImportablePaymentMethod &&
        !errorImportablePaymentMethod);

    const onHandleSelectGatewayId = () => {
      setStep(ImportStep.SELECT_GATEWAY_ACCOUNT_ID);
    };
    const onHandleImport = () => {
      doSaveImportPaymentMethod({
        accountId,
        gatewayId: watchGatewayId,
        gatewayAccountId: watchGatewayAcccountId,
        externalPaymentMethodIds: selectedPMs,
      });
    };
    const onHandleGoBack = () => {
      setStep(ImportStep.SELECT_PAYMENT_METHOD);
    };

    const onSubmit = async (requestData: IImportPaymentMethodReqSchema) => {
      doGetImportablePaymentMethod({ ...requestData, accountId });
    };

    let errorImportablePaymentMethodMessage =
      errorImportablePaymentMethod?.response?.data?.message;
    let importablePaymentMethodAlertType: 'error' | 'warning' = 'error';
    if (errorImportablePaymentMethod) {
      errorImportablePaymentMethodMessage =
        errorImportablePaymentMethod.response?.data?.message;
    } else if (dataImportablePaymentMethod?.paymentMethods.length === 0) {
      errorImportablePaymentMethodMessage =
        'This customer does not have any payment methods to import.';
      importablePaymentMethodAlertType = 'warning';
    }

    let modalTitle = 'Import Payment Method';
    const isSubmitSuccess =
      dataSaveImportPaymentMethod?.failedImports.length === 0 &&
      !errorSaveImportPaymentMethod;
    if (step == ImportStep.SUBMITTED_PAYMENT_METHOD) {
      modalTitle = isSubmitSuccess
        ? 'Successfully Imported Payment Method'
        : 'Error Importing Payment Method';
    }

    return (
      <MCenterModal
        isOpen={isOpen}
        onClose={onClose}
        modalTitle={modalTitle}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        isLoading={isResolvingLinkedCustomer}
        renderFooter={() => (
          <MStack
            gap={4}
            direction="row"
            align="center"
            justify="right"
            flex={1}
          >
            <MButton
              onClick={onClose}
              variant="cancel"
              minW="auto"
              isDisabled={isLoadingSaveImportPaymentMethod}
            >
              Cancel
            </MButton>
            {step === ImportStep.SELECT_GATEWAY_ACCOUNT_ID &&
              !errorImportablePaymentMethodMessage && (
                <MButton
                  variant="primary"
                  isLoading={isLoadingImportablePaymentMethod}
                  onClick={handleSubmit(onSubmit)}
                  isDisabled={!isValid}
                  type="submit"
                  minW="auto"
                >
                  Continue
                </MButton>
              )}
            {step === ImportStep.SELECT_GATEWAY_ACCOUNT_ID &&
              errorImportablePaymentMethodMessage && (
                <MButton variant="primary" onClick={onClose} minW="auto">
                  Close
                </MButton>
              )}
            {step === ImportStep.SELECT_PAYMENT_METHOD && (
              <MButton
                variant="primary"
                onClick={onHandleImport}
                type="submit"
                minW="auto"
                isLoading={isLoadingSaveImportPaymentMethod}
                isDisabled={selectedPMs.length === 0}
              >
                Import
              </MButton>
            )}
            {step === ImportStep.SUBMITTED_PAYMENT_METHOD &&
              isSubmitSuccess && (
                <MButton variant="primary" onClick={onClose} minW="auto">
                  Ok
                </MButton>
              )}
            {step === ImportStep.SUBMITTED_PAYMENT_METHOD &&
              !isSubmitSuccess && (
                <MButton variant="primary" onClick={onHandleGoBack} minW="auto">
                  Go Back
                </MButton>
              )}
          </MStack>
        )}
        modalContentProps={
          { 'data-testid': 'import-payment-method-form' } as any
        }
      >
        <MBox>
          {step === ImportStep.SELECT_GATEWAY_ACCOUNT_ID && (
            <MBox>
              <MFormField
                error={errors.gatewayAccountId}
                label="Payment Account ID"
                isRequired
                tooltip={
                  gatewayCustomerId
                    ? LINKED_PAYMENT_ACCOUNT_ID_TOOLTIP
                    : PAYMENT_ACCOUNT_ID_TOOLTIP
                }
              >
                <Controller
                  name="gatewayAccountId"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <MInput
                      {...field}
                      disabled={
                        isLoadingImportablePaymentMethod || !!gatewayCustomerId
                      }
                    />
                  )}
                />
                {!!errorImportablePaymentMethodMessage && (
                  <MAlertTag
                    type={importablePaymentMethodAlertType}
                    padding="4"
                    alignItems="flex-start"
                    mt="4"
                  >
                    <MBox ml="1">
                      <MText color="tGray.acBlack" fontSize="sm">
                        {errorImportablePaymentMethodMessage}
                      </MText>
                    </MBox>
                  </MAlertTag>
                )}
              </MFormField>
            </MBox>
          )}
          {step >= ImportStep.SELECT_PAYMENT_METHOD && (
            <>
              <MFlex alignItems={'center'} mb="6">
                <MText>
                  {dataImportablePaymentMethod?.gatewayAccount.name}
                </MText>
                <MBox px="2">
                  <MDivider
                    orientation="vertical"
                    borderColor="tPurple.base"
                    height="16px"
                  />
                </MBox>
                <MText>{dataImportablePaymentMethod?.gatewayAccount.id}</MText>
              </MFlex>
              <MBox mt={2}>
                <MText fontWeight="600" fontSize="md">
                  Linked Payment Methods
                </MText>
                <MBox my="4">
                  <VStack gap={4}>
                    <ImportPaymentMethodList
                      step={step}
                      selectedPMs={selectedPMs}
                      setSelectedPMs={setSelectedPMs}
                      dataImportablePaymentMethod={dataImportablePaymentMethod}
                      errorSaveImportPaymentMethod={
                        errorSaveImportPaymentMethod
                      }
                      dataSaveImportPaymentMethod={dataSaveImportPaymentMethod}
                    />
                  </VStack>
                </MBox>
              </MBox>
            </>
          )}
        </MBox>
      </MCenterModal>
    );
  },
);
